import { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, Star, ShieldCheck } from "lucide-react";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import { toast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { RubinotCharacterValidator, type RubinotCharacterValidatorHandle } from "@/components/RubinotCharacterValidator";

interface DuracaoConfig {
  id: string;
  duracao_dias: number;
  valor_reais: number;
  valor_coins: number;
  percentual_destaque: number;
  popular?: boolean;
}

interface RenovarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (
    duracao_config_id: string,
    metodo_pagamento: string,
    comprovante?: File,
    destaque?: boolean,
    personagem_pagamento_coins?: string,
  ) => void;
  isDestaque?: boolean;
  itemNome?: string;
  itemImageUrl?: string;
  expiraEm?: string | null;
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const RenovarDialog = ({ open, onOpenChange, onConfirm, isDestaque, itemNome, itemImageUrl, expiraEm }: RenovarDialogProps) => {
  const [configs, setConfigs] = useState<DuracaoConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [metodo, setMetodo] = useState<string>("pix");
  const [comprovante, setComprovante] = useState<File | null>(null);
  const [destaque, setDestaque] = useState<boolean>(!!isDestaque);
  const [personagem, setPersonagem] = useState("");
  const [personagemValidated, setPersonagemValidated] = useState(false);
  const [personagemLastValidated, setPersonagemLastValidated] = useState("");
  const [pendingProceed, setPendingProceed] = useState(false);
  const personagemInputRef = useRef<HTMLInputElement>(null);
  const validatorRef = useRef<RubinotCharacterValidatorHandle>(null);
  const { data: config } = useConfiguracoes();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setComprovante(null);
    setDestaque(!!isDestaque);
    setPersonagem("");
    setPersonagemValidated(false);
    setPersonagemLastValidated("");
    setPendingProceed(false);
    supabase
      .from("configuracoes_anuncios")
      .select("*")
      .eq("ativo", true)
      .order("duracao_dias", { ascending: true })
      .then(({ data }) => {
        const items = (data as unknown as DuracaoConfig[]) || [];
        setConfigs(items);
        if (items.length > 0) setSelectedId(items[0].id);
        setLoading(false);
      });
  }, [open, isDestaque]);

  const selectedConfig = configs.find(c => c.id === selectedId);
  const taxCoins = selectedConfig
    ? (destaque
      ? Math.ceil(selectedConfig.valor_coins + selectedConfig.valor_coins * (selectedConfig.percentual_destaque / 100))
      : selectedConfig.valor_coins)
    : 0;
  const taxReais = selectedConfig
    ? (destaque
      ? Number((selectedConfig.valor_reais + selectedConfig.valor_reais * (selectedConfig.percentual_destaque / 100)).toFixed(2))
      : Number(selectedConfig.valor_reais))
    : 0;

  const newExpiryDate = useMemo(() => {
    if (!selectedConfig) return null;
    const now = new Date();
    const currentExpiry = expiraEm ? new Date(expiraEm) : null;
    const isStillVigente = currentExpiry && currentExpiry > now;
    const baseDate = isStillVigente ? currentExpiry : now;
    const newDate = new Date(baseDate.getTime() + selectedConfig.duracao_dias * 24 * 60 * 60 * 1000);
    return newDate;
  }, [selectedConfig, expiraEm]);

  const proceedConfirm = () => {
    onConfirm(
      selectedId,
      metodo,
      comprovante || undefined,
      destaque,
      metodo === "coins" ? personagem.trim() : undefined,
    );
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (!selectedId) return;
    if (metodo === "coins") {
      if (!comprovante) {
        toast({ title: "Envie o comprovante de transferência de coins", variant: "destructive" });
        return;
      }
      const nome = personagem.trim();
      if (!nome) {
        toast({ title: "Informe o personagem que fez a transferência", variant: "destructive" });
        personagemInputRef.current?.focus();
        return;
      }
      if (!personagemValidated || personagemLastValidated !== nome) {
        setPendingProceed(true);
        validatorRef.current?.validate(nome);
        return;
      }
    }
    proceedConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Renovar Anúncio</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Item info & dates */}
            {itemNome && (
              <div className="bg-secondary/50 rounded-md p-3 space-y-2 border border-border/50">
                <div className="flex items-center gap-3">
                  {itemImageUrl && (
                    <div className="flex-shrink-0 bg-secondary/60 border border-border/40 rounded p-1.5">
                      <img
                        src={itemImageUrl}
                        alt={itemNome}
                        className="w-12 h-12 object-contain image-rendering-pixelated"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    </div>
                  )}
                  <p className="text-sm font-medium text-foreground">{itemNome}</p>
                </div>
                {expiraEm && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>Expira em:</span>
                    <span className={`font-medium ${new Date(expiraEm) < new Date() ? "text-destructive" : "text-foreground"}`}>
                      {formatDate(expiraEm)}
                    </span>
                  </div>
                )}
                {newExpiryDate && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>Nova expiração:</span>
                    <span className="font-medium text-green-400">{formatDate(newExpiryDate.toISOString())}</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-sm font-medium mb-2 block">Duração</Label>
              <div className="space-y-2">
                {configs.map((c) => (
                  <label
                    key={c.id}
                    className={`relative flex items-center justify-between p-3 rounded-md border cursor-pointer transition-colors overflow-hidden ${
                      selectedId === c.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    {c.popular && (
                      <span className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                        <span className="bg-gradient-to-r from-yellow-700 via-yellow-500 to-yellow-700 text-primary-foreground text-[8px] font-bold uppercase tracking-wider py-[2px] px-3 rounded-sm shadow-[0_2px_6px_rgba(0,0,0,0.5)] border border-yellow-300/40">
                          ★ Popular
                        </span>
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="duracao"
                        checked={selectedId === c.id}
                        onChange={() => setSelectedId(c.id)}
                        className="accent-primary"
                      />
                      <span className="text-sm font-medium">{c.duracao_dias} dias</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      R$ {Number(c.valor_reais).toFixed(2)} / {c.valor_coins} coins
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Destaque toggle */}
            <div className="flex items-center justify-between p-3 rounded-md border border-border">
              <div className="flex items-center gap-2">
                <Star className={`w-4 h-4 ${destaque ? "text-primary fill-primary" : "text-muted-foreground"}`} />
                <div>
                  <p className="text-sm font-medium">Destaque</p>
                  {selectedConfig && selectedConfig.percentual_destaque > 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      +{selectedConfig.percentual_destaque}% sobre o valor base
                    </p>
                  )}
                </div>
              </div>
              <Switch checked={destaque} onCheckedChange={setDestaque} />
            </div>

            {/* Valor total */}
            {selectedConfig && (
              <div className="bg-primary/5 rounded-md p-2 border border-primary/20 text-center">
                <p className="text-xs text-muted-foreground">Valor da renovação:</p>
                <p className="text-sm font-bold text-primary">
                  {metodo === "pix"
                    ? `R$ ${taxReais.toFixed(2)}`
                    : `${taxCoins} Coins`}
                </p>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium mb-2 block">Método de pagamento</Label>
              <div className="flex gap-2">
                {["pix", "coins"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setMetodo(m); setComprovante(null); }}
                    className={`flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors ${
                      metodo === m
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                  >
                    {m === "pix" ? "PIX" : "Coins"}
                  </button>
                ))}
              </div>
            </div>

            {/* Coins payment instructions & upload */}
            {metodo === "coins" && (
              <div className="space-y-3">
                <div className="bg-primary/5 rounded-md p-3 space-y-2 border border-primary/20">
                  <p className="text-sm font-medium text-primary">📋 Instruções de Pagamento</p>
                  <p className="text-xs text-muted-foreground">
                    Transfira <span className="text-primary font-bold">{taxCoins} Coins</span> no jogo para o personagem:
                  </p>
                  <p className="text-sm text-center text-primary font-bold bg-secondary/50 rounded-md py-2 px-3 border border-primary/20">
                    {config?.personagem_coletor || "Carregando..."}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Após a transferência, envie o print como comprovante abaixo.
                  </p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Personagem que fez a transferência
                  </Label>
                  <div className="relative">
                    <input
                      ref={personagemInputRef}
                      type="text"
                      value={personagem}
                      onChange={(e) => {
                        setPersonagem(e.target.value);
                        if (personagemValidated && e.target.value.trim() !== personagemLastValidated) {
                          setPersonagemValidated(false);
                        }
                      }}
                      placeholder="Nome do personagem no jogo"
                      maxLength={80}
                      className="w-full px-3 py-2 pr-9 text-sm rounded-md border border-border bg-secondary/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    {personagemValidated && personagem.trim() === personagemLastValidated && (
                      <ShieldCheck className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500 pointer-events-none" />
                    )}
                  </div>
                </div>

                {comprovante && comprovante.type.startsWith("image/") && (
                  <div className="flex justify-center mb-2">
                    <img src={URL.createObjectURL(comprovante)} alt="Preview" className="w-20 h-20 object-cover rounded-sm border border-border" />
                  </div>
                )}
                <label className="flex items-center gap-2 cursor-pointer w-full justify-center p-3 rounded-md border border-border hover:border-primary/50 transition-colors">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {comprovante ? comprovante.name : "Upload do Comprovante"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setComprovante(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedId || loading || (metodo === "coins" && (!comprovante || !personagem.trim()))}
          >
            Confirmar Renovação
          </Button>
        </DialogFooter>

        <RubinotCharacterValidator
          ref={validatorRef}
          requireValidation={true}
          onConfirm={(info) => {
            setPersonagem(info.name);
            setPersonagemLastValidated(info.name);
            setPersonagemValidated(true);
            if (pendingProceed) {
              setPendingProceed(false);
              // Defer slightly so state updates settle before closing
              setTimeout(() => proceedConfirm(), 0);
            }
          }}
          onCorrect={() => {
            setPendingProceed(false);
            personagemInputRef.current?.focus();
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default RenovarDialog;
